import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { logger } from '../utils/logger';
import { onlineManager } from '@tanstack/react-query';
import { db } from '../lib/offline-db';
import { processOfflineQueue } from '../lib/offline-sync';
import { debounce } from '../lib/utils';

/**
 * Reasons for triggering a refresh of content
 */
export type RefreshTriggerReason = 'FOCUS' | 'RECONNECT' | 'VISIBILITY_CHANGE' | 'MANUAL';

/**
 * Status of offline sync operation
 */
export type SyncStatus = 'idle' | 'syncing' | 'completed' | 'error';

/**
 * Interface for the connection state
 */
export interface ConnectionState {
  /** Whether the client is online */
  isOnline: boolean;
  /** Whether the tab/window is visible */
  isVisible: boolean;
  /** Timestamp of the last refresh */
  lastRefreshTimestamp: number | null;
  /** Whether a refresh is pending */
  shouldRefresh: boolean;
  /** Reason for the current refresh, if one is pending */
  refreshTrigger: RefreshTriggerReason | null;
  /** Function to mark the current refresh as complete */
  markRefreshComplete: () => void;
  /** Function to trigger a new refresh */
  triggerRefresh: (reason: RefreshTriggerReason) => void;
  /** Whether there are pending offline changes to be synced */
  hasOfflineChanges: boolean;
  /** Function to manually trigger sync of offline changes */
  syncOfflineChanges: () => Promise<void>;
  /** Current status of offline sync operation */
  syncStatus: SyncStatus;
}

// Global reference for non-hook access (e.g., React Query config)
let globalConnectionState: ConnectionState | null = null;

const ConnectionStateContext = createContext<ConnectionState | null>(null);

export interface ConnectionStateProviderProps {
  children: ReactNode;
}

/**
 * Non-hook function to get the current connection state
 * This can be used in non-React contexts or where hooks can't be used
 * This is particularly useful for:
 * - React Query's staleTime and other configuration settings
 * - Background tasks or utilities that need connection info
 * - Helper functions that don't have access to React context
 *
 * IMPORTANT: This function must be called after the ConnectionStateProvider
 * has been initialized and mounted in the component tree, otherwise it will return null.
 *
 * @returns The current connection state or null if not initialized
 */
export const getConnectionState = (): ConnectionState | null => {
  return globalConnectionState;
};

/**
 * Provider component that manages connection state
 * All hooks are called unconditionally here - no conditional returns before hooks
 */
export function ConnectionStateProvider({ children }: ConnectionStateProviderProps) {
  // Generate a unique ID for this instance to track across re-renders
  const instanceIdRef = useRef<string>(Math.random().toString(36).substring(2, 9));

  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [isVisible, setIsVisible] = useState<boolean>(
    typeof document !== 'undefined' ? document.visibilityState === 'visible' : true
  );
  const [shouldRefresh, setShouldRefresh] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<RefreshTriggerReason | null>(null);
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState<number | null>(null);
  const [hasOfflineChanges, setHasOfflineChanges] = useState<boolean>(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');

  // Track previous online state to detect reconnection
  const wasOfflineRef = useRef<boolean>(false);

  // Log initial state (only in development and only once)
  useEffect(() => {
    // Synchronize initial state with React Query
    onlineManager.setOnline(isOnline);

    if (process.env.NODE_ENV === 'development') {
      logger.component('ConnectionState initialized', {
        instanceId: instanceIdRef.current,
        isVisible,
        isOnline,
        timestamp: new Date().toISOString(),
        reactQueryOnlineState: onlineManager.isOnline(),
      });
    }
    // Only run once on mount
  }, []);

  /**
   * Check for pending offline operations
   */
  const checkOfflineChanges = useCallback(async () => {
    try {
      const count = await db.offlineOperations.where('synced').equals(0).count();

      setHasOfflineChanges(prevHasChanges => {
        const newHasChanges = count > 0;

        if (prevHasChanges !== newHasChanges || Math.random() < 0.05) {
          if (process.env.NODE_ENV === 'development') {
            logger.connectivity('Checked offline changes', {
              pendingCount: count,
              instanceId: instanceIdRef.current,
              hasChanges: newHasChanges,
              changedSinceLastCheck: prevHasChanges !== newHasChanges,
            });
          }
        }
        return newHasChanges;
      });
    } catch (error) {
      try {
        logger.error('Error checking offline changes:', error);
      } catch {
        console.error('Error while logging:', error);
      }
      setHasOfflineChanges(false);
    }
  }, []);

  /**
   * Triggers a refresh with the specified reason
   */
  const triggerRefresh = useCallback((reason: RefreshTriggerReason): void => {
    if ((process.env.NODE_ENV === 'development' && Math.random() < 0.5) || reason === 'RECONNECT') {
      try {
        logger.component('triggerRefresh called', {
          instanceId: instanceIdRef.current,
          reason,
          timestamp: new Date().toISOString(),
        });
      } catch {
        // Ignore logging errors
      }
    }

    setShouldRefresh(true);
    setRefreshTrigger(reason);
    setLastRefreshTimestamp(Date.now());
  }, []);

  /**
   * Sync offline changes when coming back online
   */
  const syncOfflineChanges = useCallback(async () => {
    if (!isOnline) {
      if (process.env.NODE_ENV === 'development') {
        logger.connectivity('Sync skipped - offline', {
          instanceId: instanceIdRef.current,
        });
      }
      return;
    }

    try {
      setSyncStatus('syncing');

      if (process.env.NODE_ENV === 'development') {
        logger.connectivity('Starting offline changes sync', {
          instanceId: instanceIdRef.current,
        });
      }

      const results = await processOfflineQueue();

      if (process.env.NODE_ENV === 'development' || results.processed > 0) {
        logger.connectivity('Offline sync completed', {
          instanceId: instanceIdRef.current,
          processed: results.processed,
          failed: results.failed,
          total: results.total,
        });
      }

      await checkOfflineChanges();

      if (results.processed > 0) {
        triggerRefresh('RECONNECT');
      }

      setSyncStatus('completed');
    } catch (error) {
      try {
        logger.error('Error syncing offline changes:', error);
      } catch {
        console.error('Error while logging:', error);
      }
      setSyncStatus('error');
    }
  }, [isOnline, checkOfflineChanges, triggerRefresh]);

  /**
   * Marks the current refresh operation as complete
   */
  const markRefreshComplete = useCallback((): void => {
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.33) {
      try {
        logger.component('markRefreshComplete called', {
          instanceId: instanceIdRef.current,
          previousTrigger: refreshTrigger,
          timestamp: new Date().toISOString(),
        });
      } catch {
        // Ignore logging errors
      }
    }

    setShouldRefresh(false);
    setRefreshTrigger(null);
  }, [refreshTrigger]);

  // Handle online/offline events
  useEffect(() => {
    const logOnlineStatus = debounce((isNowOnline: boolean) => {
      if (isNowOnline) {
        logger.connectivity('Device came online', {
          instanceId: instanceIdRef.current,
          timestamp: new Date().toISOString(),
        });
      } else {
        logger.connectivity('Device went offline', {
          instanceId: instanceIdRef.current,
          timestamp: new Date().toISOString(),
        });
      }
    }, 1000);

    const handleOnline = (): void => {
      setIsOnline(true);
      onlineManager.setOnline(true);
      logOnlineStatus(true);

      if (wasOfflineRef.current) {
        logger.connectivity('Reconnected after being offline - triggering data refresh', {
          instanceId: instanceIdRef.current,
        });

        checkOfflineChanges().then(() => {
          triggerRefresh('RECONNECT');
          wasOfflineRef.current = false;
        });
      }
    };

    const handleOffline = (): void => {
      setIsOnline(false);
      wasOfflineRef.current = true;
      onlineManager.setOnline(false);
      logOnlineStatus(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    checkOfflineChanges();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [triggerRefresh, checkOfflineChanges]);

  // Trigger sync when coming online with pending changes
  useEffect(() => {
    if (isOnline && hasOfflineChanges && syncStatus === 'idle') {
      logger.connectivity('Auto-triggering offline sync after coming online', {
        instanceId: instanceIdRef.current,
      });
      syncOfflineChanges();
    }
  }, [isOnline, hasOfflineChanges, syncOfflineChanges, syncStatus]);

  // Handle visibility change events
  useEffect(() => {
    const handleVisibilityChange = (): void => {
      const isNowVisible = document.visibilityState === 'visible';
      const previousVisibleState = isVisible;

      if (process.env.NODE_ENV === 'development' && Math.random() < 0.33) {
        try {
          logger.component('Visibility state change detected', {
            instanceId: instanceIdRef.current,
            timestamp: new Date().toISOString(),
            previousVisibleState,
            newVisibleState: isNowVisible,
            visibilityState: document.visibilityState,
          });
        } catch {
          // Ignore logging errors
        }
      }

      setIsVisible(isNowVisible);

      if (isNowVisible && !previousVisibleState) {
        if (process.env.NODE_ENV === 'development') {
          try {
            logger.component('Tab became visible, triggering VISIBILITY_CHANGE refresh', {
              instanceId: instanceIdRef.current,
              timestamp: new Date().toISOString(),
            });
          } catch {
            // Ignore logging errors
          }
        }

        try {
          checkOfflineChanges();
        } catch (error) {
          console.error('Error checking offline changes on visibility change:', error);
        }

        setTimeout(() => {
          try {
            triggerRefresh('VISIBILITY_CHANGE');
          } catch (error) {
            console.error('Error triggering refresh on visibility change:', error);
          }
        }, 0);
      }
    };

    const handlePageShow = (e: PageTransitionEvent): void => {
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.33) {
        try {
          logger.component('PageShow event detected', {
            instanceId: instanceIdRef.current,
            timestamp: new Date().toISOString(),
            persisted: e.persisted,
            visibilityState: document.visibilityState,
          });
        } catch {
          // Ignore logging errors
        }
      }

      if (e.persisted) {
        setIsVisible(true);
        checkOfflineChanges();

        logger.component('Page restored from bfcache, triggering refresh', {
          instanceId: instanceIdRef.current,
          timestamp: new Date().toISOString(),
        });

        setTimeout(() => {
          triggerRefresh('VISIBILITY_CHANGE');
        }, 0);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isVisible, triggerRefresh, checkOfflineChanges]);

  // Memoize the connection state object to prevent unnecessary re-renders
  const connectionState = useMemo<ConnectionState>(
    () => ({
      isOnline,
      isVisible,
      shouldRefresh,
      refreshTrigger,
      lastRefreshTimestamp,
      markRefreshComplete,
      triggerRefresh,
      hasOfflineChanges,
      syncOfflineChanges,
      syncStatus,
    }),
    [
      isOnline,
      isVisible,
      shouldRefresh,
      refreshTrigger,
      lastRefreshTimestamp,
      markRefreshComplete,
      triggerRefresh,
      hasOfflineChanges,
      syncOfflineChanges,
      syncStatus,
    ]
  );

  // Update global reference for non-hook access
  useEffect(() => {
    globalConnectionState = connectionState;

    if (process.env.NODE_ENV === 'development') {
      logger.component('Global ConnectionState reference updated', {
        instanceId: instanceIdRef.current,
        timestamp: new Date().toISOString(),
      });
    }
  }, [connectionState]);

  return (
    <ConnectionStateContext.Provider value={connectionState}>
      {children}
    </ConnectionStateContext.Provider>
  );
}

/**
 * Hook to access the connection state from context.
 * This is the preferred way for components to access connection state.
 */
export const useAppConnectionState = (): ConnectionState => {
  const context = useContext(ConnectionStateContext);
  if (!context) {
    throw new Error('useAppConnectionState must be used within a ConnectionStateProvider');
  }
  return context;
};

/**
 * @deprecated Use useAppConnectionState instead.
 * This export is kept for backward compatibility.
 */
export const useConnectionState = useAppConnectionState;
